import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import type { IHttpClient } from '../IHttpClient'
import {
  DownloadedOriginal,
  IUserGeometriesWebClient,
  UserGeometrySyncRequest,
  UserGeometrySyncResponse
} from './IUserGeometriesWebClient'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'

export const USER_GEOMETRIES_SYNC_TIMEOUT_MS = 30000

const PROCESSED_MIME = 'application/x-flatgeobuf'
const ORIGINAL_MIME = 'application/octet-stream'
const DEFAULT_ORIGINAL_EXTENSION = 'kml'

type UserGeometriesErrorBody = { error?: string }

@injectable()
export class UserGeometriesWebClient implements IUserGeometriesWebClient {
  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    return `${NEXATLAS_API_BASE}/userGeometry`
  }

  private authHeader(): Record<string, string> {
    return { Authorization: `Token ${this.authState.getStateSnapshot().token}` }
  }

  async sync(request: UserGeometrySyncRequest): Promise<Result<UserGeometrySyncResponse>> {
    const response = await this.httpClient.post<UserGeometrySyncResponse | UserGeometriesErrorBody>(
      `${this.getRemoteUrl()}/sync`,
      request,
      {
        headers: this.authHeader(),
        timeout: USER_GEOMETRIES_SYNC_TIMEOUT_MS
      }
    )

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      const errorCode = (response.response as UserGeometriesErrorBody)?.error || 'SYNC_FAILED'
      return Result.fail(errorCode)
    }

    return Result.ok(response.response as UserGeometrySyncResponse)
  }

  async uploadProcessed(geometryId: string, data: Blob | Uint8Array): Promise<Result<void>> {
    return this._upload(`${this.getRemoteUrl()}/${geometryId}/file`, data, PROCESSED_MIME, `${geometryId}.fgb`)
  }

  async uploadOriginal(geometryId: string, data: Blob | Uint8Array, ext?: string): Promise<Result<void>> {
    const fileName = `${geometryId}.${ext || DEFAULT_ORIGINAL_EXTENSION}`
    return this._upload(`${this.getRemoteUrl()}/${geometryId}/original`, data, ORIGINAL_MIME, fileName)
  }

  private async _upload(url: string, data: Blob | Uint8Array, mime: string, fileName: string): Promise<Result<void>> {
    const formData = new FormData()
    formData.append('file', new Blob([data as BlobPart], { type: mime }), fileName)

    const response = await this.httpClient.post<UserGeometriesErrorBody>(
      url,
      formData as unknown as Record<string, any>,
      { headers: this.authHeader() }
    )

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      const errorCode = (response.response as UserGeometriesErrorBody)?.error || 'UPLOAD_FAILED'
      return Result.fail(errorCode)
    }

    return Result.ok()
  }

  async downloadProcessed(geometryId: string): Promise<Result<Blob>> {
    const response = await this.httpClient.get<Blob>(`${this.getRemoteUrl()}/${geometryId}/file`, {
      headers: this.authHeader(),
      responseType: 'blob'
    })

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('DOWNLOAD_FAILED')
    }

    const blob = response.response
    if (!blob || blob.size === 0) {
      return Result.fail('DOWNLOAD_FAILED')
    }

    return Result.ok(blob)
  }

  async downloadOriginal(geometryId: string): Promise<Result<DownloadedOriginal>> {
    const response = await this.httpClient.get<Blob>(`${this.getRemoteUrl()}/${geometryId}/original`, {
      headers: this.authHeader(),
      responseType: 'blob'
    })

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('DOWNLOAD_FAILED')
    }

    const ext = resolveOriginalExtension(response.headers)
    return Result.ok({ blob: response.response, ext })
  }
}

// Resolves the original file's extension from the download response headers:
// the Content-Disposition filename wins, then the Content-Type, then `kml`.
export function resolveOriginalExtension(headers: Record<string, any>): string {
  const fromDisposition = extensionFromContentDisposition(headerValue(headers, 'content-disposition'))
  if (fromDisposition) return fromDisposition

  const fromContentType = extensionFromContentType(headerValue(headers, 'content-type'))
  if (fromContentType) return fromContentType

  return DEFAULT_ORIGINAL_EXTENSION
}

function headerValue(headers: Record<string, any> | undefined, name: string): string | undefined {
  if (!headers) return undefined
  const lowerName = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lowerName) {
      const value = headers[key]
      if (value == null) return undefined
      return typeof value === 'string' ? value : String(value)
    }
  }
  return undefined
}

function extensionFromContentDisposition(disposition?: string): string | null {
  if (!disposition) return null
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  if (!match) return null

  let fileName = match[1].trim()
  try {
    fileName = decodeURIComponent(fileName)
  } catch {
    // Keep the raw filename if it is not valid percent-encoding.
  }

  const dot = fileName.lastIndexOf('.')
  if (dot < 0 || dot === fileName.length - 1) return null
  return fileName.slice(dot + 1).toLowerCase()
}

function extensionFromContentType(contentType?: string): string | null {
  if (!contentType) return null
  const value = contentType.toLowerCase()
  if (value.includes('kmz') || value.includes('zip')) return 'kmz'
  if (value.includes('kml') || value.includes('xml')) return 'kml'
  return null
}
